/**
 * SessionChatInput Component
 *
 * Chat input with file upload, slash commands, and input history.
 * Ported from V1 ChatInput with Tailwind gray-* classes for V2.
 *
 * History Features:
 * - Agent-specific history (each agent has its own history)
 * - Tab synchronization via storage events
 * - Debounced saves (300ms)
 * - 30-day retention for unused histories
 * - 100 entries per agent max
 *
 * @module components/session/SessionChatInput
 */

import { useState, useRef, useEffect, memo, useCallback } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import { Send, Square, Command, Paperclip, X, Image, FileText } from 'lucide-react';

// History configuration
const HISTORY_KEY_PREFIX = 'claude-webui-v2-history-';
const HISTORY_META_KEY = 'claude-webui-v2-history-meta';
const MAX_HISTORY_PER_AGENT = 100;
const HISTORY_RETENTION_DAYS = 30;
const SAVE_DEBOUNCE_MS = 300;

// File upload configuration
const MAX_FILES = 5;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

// Allowed file types
const ALLOWED_EXTENSIONS = [
  // Images
  '.png', '.jpg', '.jpeg', '.gif', '.webp',
  // Text/Code
  '.txt', '.md', '.csv', '.html', '.css', '.js', '.ts', '.jsx', '.tsx',
  '.json', '.xml', '.yaml', '.yml', '.py', '.go', '.rs', '.java', '.c',
  '.cpp', '.h', '.sh', '.bash', '.zsh', '.toml', '.ini', '.conf', '.sql',
  '.rb', '.php', '.swift', '.kt', '.scala', '.r', '.lua', '.pl', '.pm'
];

// ============================================
// History Management Functions
// ============================================

/**
 * Get the localStorage key for an agent's history
 */
function getHistoryKey(agentId) {
  return `${HISTORY_KEY_PREFIX}${agentId}`;
}

/**
 * Load history for a specific agent
 */
function loadAgentHistory(agentId) {
  if (!agentId) return [];
  try {
    const stored = localStorage.getItem(getHistoryKey(agentId));
    if (!stored) return [];
    const data = JSON.parse(stored);
    // Handle both old format (array) and new format (object with entries and lastAccess)
    if (Array.isArray(data)) {
      return data;
    }
    return data.entries || [];
  } catch {
    return [];
  }
}

/**
 * Save history for a specific agent (with metadata)
 */
function saveAgentHistory(agentId, history) {
  if (!agentId) return;
  try {
    const data = {
      entries: history.slice(0, MAX_HISTORY_PER_AGENT),
      lastAccess: Date.now()
    };
    localStorage.setItem(getHistoryKey(agentId), JSON.stringify(data));
    updateHistoryMeta(agentId);
  } catch {}
}

/**
 * Update the meta-index of all agent histories (for cleanup)
 */
function updateHistoryMeta(agentId) {
  try {
    const meta = JSON.parse(localStorage.getItem(HISTORY_META_KEY) || '{}');
    meta[agentId] = Date.now();
    localStorage.setItem(HISTORY_META_KEY, JSON.stringify(meta));
  } catch {}
}

/**
 * Clean up old histories (older than retention period)
 * Called on component mount
 */
function cleanupOldHistories() {
  try {
    const meta = JSON.parse(localStorage.getItem(HISTORY_META_KEY) || '{}');
    const now = Date.now();
    const retentionMs = HISTORY_RETENTION_DAYS * 24 * 60 * 60 * 1000;
    let changed = false;

    for (const [agentId, lastAccess] of Object.entries(meta)) {
      if (now - lastAccess > retentionMs) {
        localStorage.removeItem(getHistoryKey(agentId));
        delete meta[agentId];
        changed = true;
      }
    }

    if (changed) {
      localStorage.setItem(HISTORY_META_KEY, JSON.stringify(meta));
    }
  } catch {}
}

/**
 * Clear history for a specific agent
 */
function clearAgentHistory(agentId) {
  if (!agentId) return;
  try {
    localStorage.removeItem(getHistoryKey(agentId));
    const meta = JSON.parse(localStorage.getItem(HISTORY_META_KEY) || '{}');
    delete meta[agentId];
    localStorage.setItem(HISTORY_META_KEY, JSON.stringify(meta));
  } catch {}
}

/**
 * Get history stats for an agent (for settings display)
 */
function getAgentHistoryStats(agentId) {
  if (!agentId) return { count: 0, lastAccess: null };
  try {
    const stored = localStorage.getItem(getHistoryKey(agentId));
    if (!stored) return { count: 0, lastAccess: null };
    const data = JSON.parse(stored);
    if (Array.isArray(data)) {
      return { count: data.length, lastAccess: null };
    }
    return {
      count: data.entries?.length || 0,
      lastAccess: data.lastAccess || null
    };
  } catch {
    return { count: 0, lastAccess: null };
  }
}

// Export for use in Agent Settings
export { clearAgentHistory, getAgentHistoryStats };

// Built-in UI slash commands
const UI_COMMANDS = [
  { name: 'compact', description: 'Compact context (summarize conversation)', isBuiltin: true },
  { name: 'help', description: 'Show available commands', isBuiltin: true },
  { name: 'clear', description: 'Clear chat history', isBuiltin: true },
  { name: 'history', description: 'Search input history (/history <term>)', isBuiltin: true },
];

// Commands blacklist (TUI-only or handled differently)
const COMMAND_BLACKLIST = [
  'help', 'clear', 'config', 'model', 'permissions',
  'login', 'logout', 'status', 'vim', 'terminal-setup', 'ide',
];

function SessionChatInput({
  onSend,
  onStop,
  disabled,
  isProcessing,
  placeholder = 'Type a message...',
  agentId,
  availableCommands = []
}) {
  // Use uncontrolled input for performance
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const commandRefs = useRef([]);
  const saveTimeoutRef = useRef(null);

  const [showCommands, setShowCommands] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [filteredCommands, setFilteredCommands] = useState(UI_COMMANDS);
  const [inputHistory, setInputHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [savedInput, setSavedInput] = useState('');
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  // Load agent-specific history on mount and when agentId changes
  useEffect(() => {
    if (agentId) {
      const history = loadAgentHistory(agentId);
      setInputHistory(history);
      setHistoryIndex(-1);
      setSavedInput('');
    }
  }, [agentId]);

  // Cleanup old histories on first mount
  useEffect(() => {
    cleanupOldHistories();
  }, []);

  // Tab synchronization: listen for storage events from other tabs
  useEffect(() => {
    if (!agentId) return;

    const handleStorageChange = (e) => {
      if (e.key === getHistoryKey(agentId) && e.newValue) {
        try {
          const data = JSON.parse(e.newValue);
          const entries = Array.isArray(data) ? data : (data.entries || []);
          setInputHistory(entries);
        } catch {}
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [agentId]);

  // Debounced save to localStorage
  const debouncedSave = useCallback((newHistory) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      saveAgentHistory(agentId, newHistory);
    }, SAVE_DEBOUNCE_MS);
  }, [agentId]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Add message to history (with debounced save)
  const addToHistory = useCallback((msg) => {
    const trimmed = msg.trim();
    if (!trimmed || !agentId) return;

    setInputHistory(prev => {
      const filtered = prev.filter(h => h !== trimmed);
      const newHistory = [trimmed, ...filtered].slice(0, MAX_HISTORY_PER_AGENT);
      debouncedSave(newHistory);
      return newHistory;
    });
  }, [agentId, debouncedSave]);

  // Validate file
  const validateFile = useCallback((file) => {
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext) && !file.type.startsWith('image/')) {
      return `File type not allowed: ${ext}`;
    }
    if (file.size > MAX_FILE_SIZE) {
      return `File too large: ${(file.size / 1024 / 1024).toFixed(1)}MB (max 10MB)`;
    }
    return null;
  }, []);

  // Process and add files
  const processFiles = useCallback((files) => {
    setUploadError(null);
    const fileArray = Array.from(files);
    const remaining = MAX_FILES - attachedFiles.length;

    if (fileArray.length > remaining) {
      setUploadError(`Only ${remaining} more file(s) can be added (max ${MAX_FILES})`);
      return;
    }

    const newFiles = [];
    for (const file of fileArray) {
      const error = validateFile(file);
      if (error) {
        setUploadError(error);
        return;
      }

      const isImage = file.type.startsWith('image/');
      const fileData = {
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        isImage,
        preview: isImage ? URL.createObjectURL(file) : null,
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      };
      newFiles.push(fileData);
    }

    setAttachedFiles(prev => [...prev, ...newFiles]);
  }, [attachedFiles.length, validateFile]);

  // Remove attached file
  const removeFile = useCallback((id) => {
    setAttachedFiles(prev => {
      const file = prev.find(f => f.id === id);
      if (file?.preview) {
        URL.revokeObjectURL(file.preview);
      }
      return prev.filter(f => f.id !== id);
    });
  }, []);

  // Handle paste event
  const handlePaste = useCallback((e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const files = [];
    for (const item of items) {
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) files.push(file);
      }
    }

    if (files.length > 0) {
      e.preventDefault();
      processFiles(files);
    }
  }, [processFiles]);

  // Handle drag and drop
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      processFiles(files);
    }
  }, [processFiles]);

  // Handle file input change
  const handleFileSelect = useCallback((e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      processFiles(files);
    }
    e.target.value = '';
  }, [processFiles]);

  // Cleanup previews on unmount
  useEffect(() => {
    return () => {
      attachedFiles.forEach(f => {
        if (f.preview) URL.revokeObjectURL(f.preview);
      });
    };
  }, []);

  // Get/set message from textarea
  const getMessage = () => textareaRef.current?.value || '';
  const setMessage = (val) => {
    if (textareaRef.current) {
      textareaRef.current.value = val;
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const message = getMessage();
    if (message.trim() || attachedFiles.length > 0) {
      addToHistory(message);
      onSend(message, attachedFiles);
      setMessage('');
      setAttachedFiles([]);
      setShowCommands(false);
      setHistoryIndex(-1);
      setSavedInput('');
      setUploadError(null);
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const selectCommand = (cmdName) => {
    setMessage(`/${cmdName} `);
    setShowCommands(false);
    textareaRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    // ESC to stop generation
    if (e.key === 'Escape') {
      if (isProcessing && onStop) {
        e.preventDefault();
        onStop();
        return;
      }
      if (showCommands) {
        setShowCommands(false);
        return;
      }
    }

    // Handle command selection
    if (showCommands) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(i => Math.min(i + 1, filteredCommands.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(i => Math.max(i - 1, 0));
        return;
      }
      if (e.key === 'Tab' || (e.key === 'Enter' && filteredCommands.length > 0)) {
        e.preventDefault();
        selectCommand(filteredCommands[selectedIndex].name);
        return;
      }
    }

    // Arrow up/down for history navigation
    if (!showCommands && inputHistory.length > 0) {
      const message = getMessage();
      if (e.key === 'ArrowUp') {
        const textarea = textareaRef.current;
        if (textarea && (textarea.selectionStart === 0 || message === '')) {
          e.preventDefault();
          if (historyIndex === -1) {
            setSavedInput(message);
          }
          const newIndex = Math.min(historyIndex + 1, inputHistory.length - 1);
          setHistoryIndex(newIndex);
          setMessage(inputHistory[newIndex]);
          return;
        }
      }
      if (e.key === 'ArrowDown' && historyIndex >= 0) {
        e.preventDefault();
        const newIndex = historyIndex - 1;
        setHistoryIndex(newIndex);
        if (newIndex === -1) {
          setMessage(savedInput);
        } else {
          setMessage(inputHistory[newIndex]);
        }
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  // Auto-scroll to keep selected command visible
  useEffect(() => {
    if (showCommands && commandRefs.current[selectedIndex]) {
      commandRefs.current[selectedIndex].scrollIntoView({
        block: 'nearest',
        behavior: 'smooth'
      });
    }
  }, [selectedIndex, showCommands]);

  // Handle input changes for slash command detection
  const handleInput = useCallback(() => {
    const value = textareaRef.current?.value || '';

    if (value.startsWith('/')) {
      const query = value.slice(1).toLowerCase();

      const claudeCommands = (availableCommands || [])
        .filter(cmd => !COMMAND_BLACKLIST.includes(cmd.name))
        .map(cmd => ({
          name: cmd.name,
          description: cmd.description,
          argumentHint: cmd.argumentHint,
          isBuiltin: false
        }));

      const allCommands = [...UI_COMMANDS, ...claudeCommands];

      const filtered = allCommands.filter(cmd =>
        cmd.name.toLowerCase().includes(query) ||
        cmd.description.toLowerCase().includes(query)
      );
      setFilteredCommands(filtered);
      setShowCommands(filtered.length > 0);
      setSelectedIndex(0);
    } else {
      setShowCommands(false);
    }
  }, [availableCommands]);

  return (
    <form
      onSubmit={handleSubmit}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`p-4 border-t border-gray-800 bg-gray-900 transition-colors ${isDragOver ? 'bg-orange-900/20 border-orange-500/50' : ''}`}
    >
      {/* Drag overlay */}
      {isDragOver && (
        <div className="absolute inset-0 bg-orange-900/30 border-2 border-dashed border-orange-500 rounded-lg flex items-center justify-center z-20 pointer-events-none">
          <div className="text-orange-400 text-lg font-medium">Drop files here</div>
        </div>
      )}

      {/* Upload error */}
      {uploadError && (
        <div className="max-w-4xl mx-auto mb-2 px-3 py-2 bg-red-900/30 border border-red-700 rounded-lg text-red-400 text-sm flex justify-between items-center">
          <span>{uploadError}</span>
          <button type="button" onClick={() => setUploadError(null)} className="text-red-500 hover:text-red-400">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Attached files preview */}
      {attachedFiles.length > 0 && (
        <div className="max-w-4xl mx-auto mb-3 flex gap-2 flex-wrap">
          {attachedFiles.map((file) => (
            <div
              key={file.id}
              className="relative group bg-gray-800 border border-gray-700 rounded-lg overflow-hidden"
            >
              {file.isImage ? (
                <div className="w-16 h-16 relative">
                  <img
                    src={file.preview}
                    alt={file.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <button
                      type="button"
                      onClick={() => removeFile(file.id)}
                      className="p-1 bg-red-600 rounded-full text-white hover:bg-red-500"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="px-3 py-2 flex items-center gap-2 max-w-48">
                  <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="text-gray-300 text-xs truncate">{file.name}</span>
                  <button
                    type="button"
                    onClick={() => removeFile(file.id)}
                    className="p-0.5 text-gray-500 hover:text-red-400 flex-shrink-0"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}
            </div>
          ))}
          {attachedFiles.length < MAX_FILES && (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-16 h-16 border border-dashed border-gray-600 rounded-lg flex items-center justify-center text-gray-500 hover:text-gray-400 hover:border-gray-500 transition-colors"
              title="Add more files"
            >
              <Paperclip className="w-5 h-5" />
            </button>
          )}
        </div>
      )}

      <div className="flex gap-3 items-end max-w-4xl mx-auto">
        {/* File input (hidden) */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ALLOWED_EXTENSIONS.join(',')}
          onChange={handleFileSelect}
          className="hidden"
        />

        {/* Attach button */}
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || attachedFiles.length >= MAX_FILES}
          className={`p-3 rounded-xl transition-all ${
            disabled || attachedFiles.length >= MAX_FILES
              ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
              : 'bg-gray-800 text-gray-400 hover:text-orange-400 hover:bg-gray-700'
          }`}
          title={attachedFiles.length >= MAX_FILES ? `Max ${MAX_FILES} files` : 'Attach files'}
        >
          <Paperclip className="w-5 h-5" />
        </button>

        <div className="flex-1 relative">
          {/* Command autocomplete dropdown */}
          {showCommands && (
            <div className="absolute bottom-full left-0 right-0 mb-2 bg-gray-800 border border-gray-700 rounded-lg shadow-xl overflow-hidden z-10 max-h-80 overflow-y-auto">
              {/* UI Commands section */}
              {filteredCommands.filter(c => c.isBuiltin).length > 0 && (
                <>
                  <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-gray-500 bg-gray-850 border-b border-gray-700">
                    UI Commands
                  </div>
                  {filteredCommands.map((cmd, index) => {
                    if (!cmd.isBuiltin) return null;
                    return (
                      <button
                        key={cmd.name}
                        ref={el => commandRefs.current[index] = el}
                        type="button"
                        onClick={() => selectCommand(cmd.name)}
                        className={`w-full px-3 py-1.5 flex items-center gap-2 text-left transition-colors text-xs
                          ${index === selectedIndex ? 'bg-orange-600/20 text-orange-400' : 'hover:bg-gray-700 text-gray-200'}`}
                      >
                        <Command className="w-3 h-3 text-gray-500 flex-shrink-0" />
                        <span className="font-medium text-gray-100">/{cmd.name}</span>
                        <span className="text-gray-500 truncate">{cmd.description}</span>
                      </button>
                    );
                  })}
                </>
              )}
              {/* Claude Commands section */}
              {filteredCommands.filter(c => !c.isBuiltin).length > 0 && (
                <>
                  <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-gray-500 bg-gray-850 border-b border-gray-700 border-t">
                    Claude Commands
                  </div>
                  {filteredCommands.map((cmd, index) => {
                    if (cmd.isBuiltin) return null;
                    const displayName = cmd.name
                      .replace(/^taches-cc-resources:/, '')
                      .replace(/^claude-mem:/, '');
                    return (
                      <button
                        key={cmd.name}
                        ref={el => commandRefs.current[index] = el}
                        type="button"
                        onClick={() => selectCommand(cmd.name)}
                        className={`w-full px-3 py-1.5 flex items-center gap-2 text-left transition-colors text-xs
                          ${index === selectedIndex ? 'bg-orange-600/20 text-orange-400' : 'hover:bg-gray-700 text-gray-200'}`}
                      >
                        <Command className="w-3 h-3 text-orange-500/50 flex-shrink-0" />
                        <span className="font-medium text-gray-100 flex-shrink-0">/{displayName}</span>
                        <span className="text-gray-500 truncate">{cmd.description}</span>
                      </button>
                    );
                  })}
                </>
              )}
            </div>
          )}

          <TextareaAutosize
            ref={textareaRef}
            defaultValue=""
            onKeyDown={handleKeyDown}
            onInput={handleInput}
            onPaste={handlePaste}
            placeholder={isProcessing ? 'Type to send a follow-up message...' : placeholder}
            disabled={disabled}
            minRows={2}
            maxRows={8}
            cacheMeasurements
            className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 pr-12 text-gray-100 placeholder-gray-500 focus:outline-none focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/20 resize-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <div className="absolute right-2 bottom-2 text-xs text-gray-600">
            {isProcessing ? 'ESC to stop' : 'Enter to send'}
          </div>
        </div>

        {isProcessing ? (
          <button
            type="button"
            onClick={onStop}
            className="p-3 rounded-xl transition-all bg-red-600 hover:bg-red-500 text-white shadow-lg shadow-red-600/20"
            title="Stop generation (ESC)"
          >
            <Square className="w-5 h-5" />
          </button>
        ) : (
          <button
            type="submit"
            disabled={disabled}
            className={`p-3 rounded-xl transition-all ${
              disabled
                ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
                : 'bg-indigo-600 hover:bg-indigo-500 text-white shadow-lg shadow-indigo-600/20'
            }`}
          >
            <Send className="w-5 h-5" />
          </button>
        )}
      </div>

      <div className="text-center mt-2 text-xs text-gray-600">
        {isProcessing ? 'Generating... Press Enter to send follow-up, ESC to stop' : 'Paste, drag & drop, or click clip to attach files'}
      </div>
    </form>
  );
}

export default memo(SessionChatInput);
